import { KitchenOrderDto, KitchenOrderStatus } from '../../types/kitchen';

export type KdsOrderWithTime = KitchenOrderDto & { elapsedSeconds: number };

// Color thresholds for elapsed time (in seconds)
export const TIME_THRESHOLDS = {
  GREEN: 10 * 60,    // 0-10 minutes: green
  YELLOW: 20 * 60,   // 10-20 minutes: yellow/orange
  // > 20 minutes: red
};

// Past this point the timer stops meaning "urgency" and starts meaning "stale data":
// the card switches to the compact +Xh Ym format and the "olvidada" treatment.
export const LATE_THRESHOLD_SECONDS = 100 * 60;

export const isLateOrder = (elapsedSeconds: number): boolean =>
  elapsedSeconds >= LATE_THRESHOLD_SECONDS;

export const getTimeColor = (elapsedSeconds: number): string => {
  if (elapsedSeconds < TIME_THRESHOLDS.GREEN) return 'var(--kds-ok-bright)';
  if (elapsedSeconds < TIME_THRESHOLDS.YELLOW) return 'var(--kds-warn)';
  return 'var(--kds-bad)';
};

export const formatElapsedTime = (seconds: number): string => {
  if (seconds >= LATE_THRESHOLD_SECONDS) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `+${hours}h ${mins}m`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const effectiveStatusOf = (order: KitchenOrderDto): string =>
  String(order.filteredStatus ?? order.status);

export const prepDurationMinutes = (sentAt: string, completedAt: string): number => {
  const diffMs = new Date(completedAt).getTime() - new Date(sentAt).getTime();
  return Math.max(0, Math.round(diffMs / 60000));
};

// Group items by productName + variants (only if they don't have notes and not cancelled)
export interface GroupedItem {
  productName: string;
  variants?: string;
  quantity: number;
  notes?: string;
  seatNumber?: number | null;
  isGrouped: boolean;
  isCancelled?: boolean;
  isCompleted?: boolean;
}

// Grouped dish across all orders (for grouped view)
export interface GroupedDish {
  dishKey: string;
  productName: string;
  variants?: string;
  totalQuantity: number;
  orders: Array<{
    orderId: string;
    tableName: string;
    areaName: string;
    quantity: number;
    notes?: string;
  }>;
  oldestSentAt: Date;
  elapsedSeconds: number;
}

// Parse the backend `variants` string for a daily-menu course row, which looks like
// "3× Sopa, 2× Consomé" (quantities prefixed with "×") or "Sopa" when qty=1.
// Returns the options with their per-option quantity so the KDS can render each one on its
// own indented line.
export interface CourseOption {
  quantity: number;
  name: string;
}

export const parseCourseOptions = (variants: string | undefined | null): CourseOption[] => {
  if (!variants) return [];
  return variants.split(',').map(raw => {
    const trimmed = raw.trim();
    const match = trimmed.match(/^(\d+)\s*[×x]\s*(.+)$/);
    if (match) {
      return { quantity: parseInt(match[1], 10), name: match[2].trim() };
    }
    return { quantity: 1, name: trimmed };
  }).filter(o => o.name.length > 0);
};

export const groupOrderItems = (
  items: { productName: string; quantity: number; variants?: string; notes?: string; seatNumber?: number | null; isCancelled?: boolean; isCompleted?: boolean }[]
): GroupedItem[] => {
  const grouped: GroupedItem[] = [];
  const groupMap = new Map<string, GroupedItem>();

  items.forEach(item => {
    // Cancelled/completed items are always shown individually (not grouped)
    // Items with notes are always shown individually
    if (item.notes || item.isCancelled || item.isCompleted) {
      grouped.push({
        productName: item.productName,
        variants: item.variants,
        quantity: item.quantity,
        notes: item.notes,
        seatNumber: item.seatNumber,
        isGrouped: false,
        isCancelled: item.isCancelled,
        isCompleted: item.isCompleted
      });
    } else {
      // Group by productName + variants + comensal (C1 y C2 con el mismo platillo no se juntan)
      const key = `${item.productName}|${item.variants || ''}|${item.seatNumber ?? ''}`;
      const existing = groupMap.get(key);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        const newItem: GroupedItem = {
          productName: item.productName,
          variants: item.variants,
          quantity: item.quantity,
          seatNumber: item.seatNumber,
          isGrouped: true,
          isCancelled: false,
          isCompleted: false
        };
        groupMap.set(key, newItem);
        grouped.push(newItem);
      }
    }
  });

  return grouped;
};

// Group all orders by dish (for grouped view - only Pending/Preparing orders)
export const groupAllOrdersByDish = (orders: KdsOrderWithTime[]): GroupedDish[] => {
  const dishMap = new Map<string, GroupedDish>();

  orders.forEach(order => {
    // Only include Pending or Preparing orders
    const statusStr = effectiveStatusOf(order);
    const isPending = statusStr === KitchenOrderStatus.Pending;
    const isPreparing = statusStr === KitchenOrderStatus.Preparing;

    if (!isPending && !isPreparing) {
      return; // Skip Ready and Completed orders
    }

    order.items.forEach(item => {
      // Skip cancelled items
      if (item.isCancelled) return;

      const dishKey = `${item.productName}|${item.variants || ''}`;

      if (dishMap.has(dishKey)) {
        const existing = dishMap.get(dishKey)!;
        existing.totalQuantity += item.quantity;
        existing.orders.push({
          orderId: order.id,
          tableName: `${order.areaName} - ${order.tableNumber}`,
          areaName: order.areaName,
          quantity: item.quantity,
          notes: item.notes,
        });

        // Update oldest time if this order is older
        const orderSentAt = new Date(order.sentAt);
        if (orderSentAt < existing.oldestSentAt) {
          existing.oldestSentAt = orderSentAt;
          existing.elapsedSeconds = order.elapsedSeconds;
        }
      } else {
        dishMap.set(dishKey, {
          dishKey,
          productName: item.productName,
          variants: item.variants,
          totalQuantity: item.quantity,
          orders: [{
            orderId: order.id,
            tableName: `${order.areaName} - ${order.tableNumber}`,
            areaName: order.areaName,
            quantity: item.quantity,
            notes: item.notes,
          }],
          oldestSentAt: new Date(order.sentAt),
          elapsedSeconds: order.elapsedSeconds,
        });
      }
    });
  });

  return Array.from(dishMap.values()).sort((a, b) =>
    b.elapsedSeconds - a.elapsedSeconds // Oldest first
  );
};
